import {
  getTaskListOverdueSeverityMs,
  getTaskListUpcomingDeadlineSortKey,
  getTemplateListDeadlineSortKey,
  resolveDeadlineInstant,
  taskListShowsOverdueBadge,
} from "./taskDeadlineDisplay";

const normalizeStatusKey = (status) =>
  String(status || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const LEGACY_OVERDUE_KEYS = new Set(["over due", "overdue"]);
const PENDING_KEYS = new Set(["pending"]);
const IN_PROGRESS_KEYS = new Set(["in progress"]);
const CANCELLED_KEYS = new Set(["cancelled", "canceled"]);
const COMPLETED_KEYS = new Set(["complete", "completed"]);

const PENDING_STATUS_RANK = 0;

export const getStatusSortRank = (status) => {
  const key = normalizeStatusKey(status);
  if (LEGACY_OVERDUE_KEYS.has(key) || PENDING_KEYS.has(key)) return 0;
  if (IN_PROGRESS_KEYS.has(key)) return 1;
  if (CANCELLED_KEYS.has(key)) return 2;
  if (COMPLETED_KEYS.has(key)) return 3;
  return 4;
};

const deadlineSortKey = (row) => {
  const d = resolveDeadlineInstant(row?.deadlineAt, row?.deadline);
  if (!d) return Number.POSITIVE_INFINITY;
  return d.getTime();
};

const createdAtSortKey = (row) => {
  const raw = row?.createdAt;
  if (!raw) return 0;
  const d = raw instanceof Date ? raw : new Date(raw);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
};

const compareRowTieBreakers = (a, b) => {
  const createdDiff = createdAtSortKey(b) - createdAtSortKey(a);
  if (createdDiff !== 0) return createdDiff;

  return String(a?.name || "").localeCompare(String(b?.name || ""), undefined, {
    sensitivity: "base",
  });
};

/** Pending tasks: upcoming date/time first (soonest), minus-badge rows last (least minus first). */
const comparePendingTaskDeadlines = (a, b, now) => {
  const overdueA = taskListShowsOverdueBadge(a, now);
  const overdueB = taskListShowsOverdueBadge(b, now);
  if (overdueA !== overdueB) return overdueA ? 1 : -1;

  if (!overdueA) {
    const keyA = getTaskListUpcomingDeadlineSortKey(a);
    const keyB = getTaskListUpcomingDeadlineSortKey(b);
    if (keyA !== keyB) return keyA - keyB;
    return 0;
  }

  const severityA = getTaskListOverdueSeverityMs(a, now);
  const severityB = getTaskListOverdueSeverityMs(b, now);
  if (severityA !== severityB) return severityA - severityB;
  return 0;
};

export const compareTaskTableRows = (a, b, now = new Date()) => {
  const rankA = getStatusSortRank(a?.status);
  const rankB = getStatusSortRank(b?.status);
  if (rankA !== rankB) return rankA - rankB;

  if (rankA === PENDING_STATUS_RANK) {
    const pendingCmp = comparePendingTaskDeadlines(a, b, now);
    if (pendingCmp !== 0) return pendingCmp;
  } else {
    const deadlineA = deadlineSortKey(a);
    const deadlineB = deadlineSortKey(b);
    if (deadlineA !== deadlineB) return deadlineA - deadlineB;
  }

  return compareRowTieBreakers(a, b);
};

export const compareTaskTemplateTableRows = (a, b) => {
  const rankA = getStatusSortRank(a?.status);
  const rankB = getStatusSortRank(b?.status);
  if (rankA !== rankB) return rankA - rankB;

  if (rankA === PENDING_STATUS_RANK) {
    const keyA = getTemplateListDeadlineSortKey(a);
    const keyB = getTemplateListDeadlineSortKey(b);
    if (keyA !== keyB) return keyA - keyB;
  } else {
    const deadlineA = deadlineSortKey(a);
    const deadlineB = deadlineSortKey(b);
    if (deadlineA !== deadlineB) return deadlineA - deadlineB;
  }

  return compareRowTieBreakers(a, b);
};

export const sortTaskTableRows = (rows) => {
  if (!Array.isArray(rows) || rows.length < 2) return rows || [];
  const now = new Date();
  return [...rows].sort((a, b) => compareTaskTableRows(a, b, now));
};

export const sortTaskTemplateTableRows = (rows) => {
  if (!Array.isArray(rows) || rows.length < 2) return rows || [];
  return [...rows].sort(compareTaskTemplateTableRows);
};

export const sortGroupedTasksData = (grouped) => {
  if (!grouped) return grouped;
  return {
    ...grouped,
    leaderTasks: sortTaskTableRows(grouped.leaderTasks || []),
    unassignedTasks: sortTaskTableRows(grouped.unassignedTasks || []),
    members: (grouped.members || []).map((bucket) => ({
      ...bucket,
      tasks: sortTaskTableRows(bucket.tasks || []),
    })),
  };
};
